import React from "react"
import { cn } from "@/lib/utils"

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Whether the section should take full screen height */
  fullHeight?: boolean
  /** Whether to enable scroll snap for this section */
  scrollSnap?: boolean
  /** Background variant */
  variant?: "default" | "gradient-blue" | "gradient-gray" | "dark" | "light"
  /** Background image URL */
  backgroundImage?: string
  /** Background image blur amount (in px) */
  backgroundBlur?: number
  /** Overlay opacity (0-100) */
  overlayOpacity?: number
  /** Overlay color */
  overlayColor?: "black" | "white" | "blue" | "transparent"
  /** Content positioning */
  contentPosition?: "center" | "top" | "bottom" | "left" | "right"
  /** Whether content should be contained in max-width container */
  contained?: boolean
  /** Padding variant */
  padding?: "none" | "sm" | "md" | "lg" | "xl"
}

const Section = React.forwardRef<HTMLElement, SectionProps>(
  ({
    className,
    fullHeight = false,
    scrollSnap = false,
    variant = "default",
    backgroundImage,
    backgroundBlur = 0,
    overlayOpacity = 40,
    overlayColor = "black",
    contentPosition = "center",
    contained = true,
    padding = "lg",
    children,
    ...props
  }, ref) => {
    const sectionClasses = cn(
      "relative w-full",
      // Full height
      fullHeight && "min-h-screen",
      // Scroll snap
      scrollSnap && "snap-start snap-always",
      // Background variants
      variant === "gradient-blue" && "bg-gradient-to-br from-harvey-blue to-harvey-skyblue",
      variant === "gradient-gray" && "bg-gradient-to-b from-gray-50 to-white",
      variant === "dark" && "bg-black",
      variant === "light" && "bg-white",
      // Content positioning
      contentPosition === "center" && "flex items-center justify-center",
      contentPosition === "top" && "flex items-start justify-center pt-20",
      contentPosition === "bottom" && "flex items-end justify-center pb-20",
      contentPosition === "left" && "flex items-center justify-start",
      contentPosition === "right" && "flex items-center justify-end",
      // Padding
      padding === "sm" && "px-4 py-8",
      padding === "md" && "px-4 sm:px-6 py-12",
      padding === "lg" && "px-4 sm:px-8 py-12 sm:py-20",
      padding === "xl" && "px-4 sm:px-8 lg:px-12 py-16 sm:py-24",
      padding === "none" && "p-0",
      className
    )

    return (
      <section ref={ref} className={sectionClasses} {...props}>
        {/* Background Image */}
          {backgroundImage && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: backgroundBlur > 0 ? `blur(${backgroundBlur}px)` : 'none'
              }}
            />
          )}         

        {/* Overlay */}
        {overlayColor !== "transparent" && (
          <div
            className={cn(
              "absolute inset-0",
              overlayColor === "black" && `bg-black/${overlayOpacity}`,
              overlayColor === "white" && `bg-white/${overlayOpacity}`,
              overlayColor === "blue" && `bg-blue-900/${overlayOpacity}`
            )}
          />
        )}

        {/* Content */}
        <div className={cn(
          "relative z-10 w-full",
          contained && "max-w-7xl mx-auto",
          contentPosition === "center" && "text-center"
        )}>
          {children}
        </div>
      </section>
    )
  }
)

Section.displayName = "Section"

export { Section }
